import { useMemoDeepEqual, useSharedMemo } from '@client/hooks/use-utils.hooks';
import { useFolderStateStore, IFolderState } from './folder.store';
import { pick } from '@shared/helpers/object.helpers';
import { IProjectFolder } from '@shared/models/folder.model';
import {
  useProjects,
  useUpdateProjectAndFolderLocations,
} from '../project/project.hook';
import { useCallback, useMemo } from 'react';
import { getProjectFolderTree } from '@shared/helpers/project-folder.helpers';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { OneOfProjectListElements } from '@shared/models/project.model';
import amplitudeLog from '@client/amplitude';
import { AMP_PROJECT_FOLDER_DELETE } from '@shared/constants';

/**
 * Create a new object including a subset of propetries from an object
 * @param obj
 * @param keys
 * @returns
 */
export function useFolderState<K extends keyof IFolderState>(
  ...keys: K[]
): Pick<IFolderState, K> {
  // Create a memoed selector function to pick properties
  const selector = useMemoDeepEqual(
    () => (o: IFolderState) => (keys.length ? pick(o, ...keys) : o),
    keys,
  );
  return useFolderStateStore(selector);
}

export const useDeleteFolder = () => {
  const deleteFolder = useFolderStateStore((state) => state.deleteFolder);
  const updateProjectAndFolderLocations = useUpdateProjectAndFolderLocations();
  const confirm = useIsConfirmed();
  const folders = useFolders();
  const projects = useProjects();

  // Old code, needs to be refactored to not run updateProjectAndFolderLocations twice
  const updateLocationsAndDelete = useCallback(
    async (id: IProjectFolder['id']) => {
      const foldersAndProjectInfos = [...folders, ...projects];
      const itemsInRoot = foldersAndProjectInfos.filter(
        ({ parent_id }) => parent_id === null,
      );
      const itemsInParent: OneOfProjectListElements[] = foldersAndProjectInfos
        .filter(({ parent_id }) => parent_id === id)
        .sort((a, b) => a.location - b.location)
        .map((item, index) => ({
          ...item,
          parent_id: null,
          location: index + (itemsInRoot.length ?? 1) - 1,
        }));

      await updateProjectAndFolderLocations(itemsInParent);
      await deleteFolder(id);
      const projectInfosInRoot = projects.filter(
        (projectInfo) => projectInfo.parent_id === null,
      );

      const foldersInRoot = folders.filter(
        (folder) => folder.id !== id && folder.parent_id === null,
      );

      const itemsWithUpdatedLocations = [
        ...projectInfosInRoot,
        ...foldersInRoot,
        ...itemsInParent,
      ]
        .sort((a, b) => a.location - b.location)
        .map((item, index) => ({ ...item, location: index }));

      await updateProjectAndFolderLocations(itemsWithUpdatedLocations);

      amplitudeLog(AMP_PROJECT_FOLDER_DELETE, {
        FolderId: id,
      });
    },
    [folders, projects, updateProjectAndFolderLocations, deleteFolder],
  );
  return useCallback(
    async (id: IProjectFolder['id']) => {
      const folder = getFolders().find((f) => f.id === id);
      if (!folder) {
        throw new Error(`Folder not found`);
      }
      const confirmed = await confirm({
        title: `Delete ${folder.name}?`,
        confirmationText: 'Delete',
        content: `This will move all the contents of the folder out to the root level and delete the folder. This action cannot be undone.`,
      });
      if (confirmed) {
        return await updateLocationsAndDelete(id);
      }
    },
    [confirm, updateLocationsAndDelete],
  );
};

const foldersSelector = (state: IFolderState): IProjectFolder[] =>
  state.folders;

/**
 * Get folders without causing rerenders
 * @returns
 */
export const getFolders = (): IProjectFolder[] =>
  foldersSelector(useFolderStateStore.getState());

export const useFolders = () =>
  useFolderStateStore((state) => foldersSelector(state));

export const useProjectInfosAndProjectFolders = () => {
  const folders = useFolders();
  const projects = useProjects();
  return useMemo(() => [...folders, ...projects], [folders, projects]);
};

export const useProjectFolderTree = (ownerFilter?: string) => {
  const folders = useFolders();
  const projects = useProjects();
  return useSharedMemo(
    () => {
      const filteredProjects = ownerFilter
        ? projects.filter(({ owner }) => owner === ownerFilter)
        : projects;
      return getProjectFolderTree([...folders, ...filteredProjects]);
    },
    `useProjectFolderTree:${ownerFilter ?? 'all'}`,
    [folders, ownerFilter, projects],
  );
};
